import type { MetaFunction } from "@remix-run/cloudflare";
import { useEffect, useRef, useState } from "react";
import { Link } from "@remix-run/react";
import { Zap, Flame, Terminal, Cpu, FileText, Info, Mail, TrendingDown, Shield, Layers, Gamepad2, Video, MessageCircle } from "lucide-react";
import { SplashScreen } from "~/components/SplashScreen";
import styles from "~/styles/launcher.module.css";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const NEWS = [
  { date: "JUN 2026", title: "Prodigium Dynamics launches its first suite of AI-native tools." },
  { date: "MAY 2026", title: "PageBurn enters public beta — reading reimagined for dense content." },
  { date: "MAY 2026", title: "Skillarq releases 12 new precision-engineered system prompts." },
  { date: "APR 2026", title: "Brubri cognitive OS opens early access for neurodivergent founders." },
  { date: "MAR 2026", title: "Naomi — our AI connection layer — enters closed development." },
];

export const meta: MetaFunction = () => [
  { title: "Prodigium Dynamics" },
  { name: "description", content: "AI-driven studio. Digital products engineered with precision." },
];

// Isolated so its 1s tick re-renders only the clock, not the whole tile grid.
// setState with an identical string is a no-op in React, so within a given
// minute the repeated updates don't even re-render this component.
function LiveClock() {
  const [time, setTime] = useState("--:--");
  const [date, setDate] = useState("");

  useEffect(() => {
    const update = () => {
      const now = new Date();
      setTime(now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false }));
      setDate(now.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }).toUpperCase());
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className={styles.tileClockBlock}>
      <p className={styles.tileClock}>{time}</p>
      <p className={styles.tileClockDate}>{date}</p>
    </div>
  );
}

export default function Launcher() {
  const [newsIdx, setNewsIdx] = useState(0);
  const [bannerIdx, setBannerIdx] = useState(0);
  const [canInstall, setCanInstall] = useState(false);
  const [installMsg, setInstallMsg] = useState("");
  const [showInstallSheet, setShowInstallSheet] = useState(false);
  const deferredPrompt = useRef<BeforeInstallPromptEvent | null>(null);

  // PWA install prompt
  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      deferredPrompt.current = e as BeforeInstallPromptEvent;
      setCanInstall(true);
    };
    const onInstalled = () => setCanInstall(false);
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt.current) {
      deferredPrompt.current.prompt();
      const { outcome } = await deferredPrompt.current.userChoice;
      if (outcome === "accepted") {
        deferredPrompt.current = null;
        setCanInstall(false);
        setShowInstallSheet(false);
      }
    } else {
      setInstallMsg("Menú del navegador → Añadir a inicio");
      setTimeout(() => {
        setInstallMsg("");
        setShowInstallSheet(false);
      }, 3500);
    }
  };

  // News carousel cycling
  useEffect(() => {
    const id = setInterval(() => setNewsIdx(i => (i + 1) % NEWS.length), 3500);
    return () => clearInterval(id);
  }, []);

  // Banner carousel cycling
  useEffect(() => {
    const id = setInterval(() => setBannerIdx(i => (i + 1) % 3), 5000);
    return () => clearInterval(id);
  }, []);


  return (
    <>
      <SplashScreen name="PRODIGIUM" tag="AI-Driven Studio" />
      <div className={styles.launcher}>
        <header className={styles.osHeader}>
          <span className={styles.osTitle}>Prodigium Dynamics</span>
        </header>

        <div className={styles.osTagline}>
          <span>All tools, apps, and layers of the Prodigium ecosystem.</span>
          <span className={styles.osVersion}>v2.6.0</span>
        </div>

        <div className={styles.grid}>

          {/* ── CLOCK WIDGET — 2×2 ──────────────────────────── */}
          <div className={`${styles.tileClk}`}>
            <LiveClock />
          </div>

          {/* ── PRODIGIUM — 2×2 ─────────────────────────────── */}
          <Link to="/studio" className={`${styles.tile} ${styles.tileProdigium}`}>
            <div className={styles.tileIcon}>
              <Zap size={16} strokeWidth={1.4} />
            </div>
            <h2 className={styles.tileName}>PRODIGIUM</h2>
            <p className={styles.tileDesc}>Precision-built digital products for the AI era.</p>
            <div className={styles.tileStatus}>
              <span className={styles.dotLive} />
              Studio
            </div>
            <span className={styles.cornerTR} />
          </Link>

          {/* ── PAGEBURN — 2×2 (external) ───────────────────── */}
          <a
            href="https://www.pageburn.site"
            target="_blank"
            rel="noopener noreferrer"
            className={`${styles.tile} ${styles.tilePageburn}`}
          >
            <div className={styles.tileIcon}>
              <Flame size={16} strokeWidth={1.4} />
            </div>
            <h3 className={styles.tileName}>PAGEBURN</h3>
            <p className={styles.tileDesc}>Reading shouldn&apos;t feel like work. Dense text, made effortless.</p>
            <div className={styles.tileStatus}>
              <span className={styles.dotLive} />
              Live ↗
            </div>
            <span className={styles.cornerTR} />
          </a>

          {/* ── SKILLARQ — 2×2 ──────────────────────────────── */}
          <Link to="/skillarq" className={`${styles.tile} ${styles.tileSkills}`}>
            <div className={styles.tileIcon}>
              <Terminal size={16} strokeWidth={1.4} />
            </div>
            <h3 className={styles.tileName}>SKILLARQ</h3>
            <p className={styles.tileDesc}>Engineered prompts packaged as .MD files. Load into any AI.</p>
            <div className={styles.tileStatus}>
              <span className={styles.dotLive} />
              Live
            </div>
            <span className={styles.cornerTR} />
          </Link>

          {/* ── BRUBRI — 2×2 ────────────────────────────────── */}
          <Link to="/brubri" className={`${styles.tile} ${styles.tileBrubri}`}>
            <div className={styles.tileIcon}>
              <Cpu size={16} strokeWidth={1.4} />
            </div>
            <h3 className={styles.tileName}>BRUBRI</h3>
            <p className={styles.tileDesc}>Task execution system built for neurodivergent minds.</p>
            <div className={styles.tileStatus}>
              <span className={styles.dotAmber} />
              Beta
            </div>
            <span className={styles.cornerTR} />
          </Link>

          {/* ── NAOMI — 1×1 (beta) ──────────────────────────── */}
          <Link to="/naomi" className={`${styles.tile} ${styles.tileNaomi}`}>

            <div className={styles.tileIcon}>
              <MessageCircle size={16} strokeWidth={1.4} />
            </div>
            <h3 className={styles.tileName}>NAOMI</h3>
            <div className={styles.tileStatus}>
              <span className={styles.dotAmber} />
              Beta
            </div>
            <span className={styles.cornerTR} />
          </Link>

          {/* ── BLOG — 1×1 ──────────────────────────────────── */}
          <Link to="/blog" className={`${styles.tile} ${styles.tileBlog}`}>

            <div className={styles.tileIcon}>
              <FileText size={16} strokeWidth={1.4} />
            </div>
            <h3 className={styles.tileName}>BLOG</h3>
          </Link>

          {/* ── ABOUT — 1×1 ─────────────────────────────────── */}
          <Link to="/about" className={`${styles.tile} ${styles.tileAbout}`}>

            <div className={styles.tileIcon}>
              <Info size={16} strokeWidth={1.4} />
            </div>
            <h3 className={styles.tileName}>ABOUT</h3>
          </Link>

          {/* ── CONTACT — 1×1 ───────────────────────────────── */}
          <Link to="/contact" className={`${styles.tile} ${styles.tileContact}`}>

            <div className={styles.tileIcon}>
              <Mail size={16} strokeWidth={1.4} />
            </div>
            <h3 className={styles.tileName}>CONTACT</h3>
          </Link>

          {/* ── BURN RATE — 1×1 ─────────────────────────────── */}
          <Link to="/burn-rate" className={`${styles.tile} ${styles.tileBurnrate}`}>

            <div className={styles.tileIcon}>
              <TrendingDown size={16} strokeWidth={1.4} />
            </div>
            <h3 className={styles.tileName}>BURN RATE</h3>
            <div className={styles.tileStatus}>
              <span className={styles.dotLive} />
              Live
            </div>
          </Link>

          {/* ── INSTALL PWA — 1×1 ──────────────────────────── */}
          <div
            role="button"
            tabIndex={0}
            onClick={() => setShowInstallSheet(true)}
            onKeyDown={(e) => e.key === "Enter" && setShowInstallSheet(true)}
            className={`${styles.tile} ${styles.tileInstall}`}
          >

            <h3 className={styles.tileName}>INSTALL APP</h3>
            <div className={styles.tileStatus}>
              {canInstall ? <><span className={styles.dotLive} />Install</> : <>+ Add to home</>}
            </div>
          </div>

          {/* ── PRODIGIUM ACCOUNT — 2×2 ─────────────────────── */}
          <Link to="/account" className={`${styles.tile} ${styles.tileAccount}`}>
            <div className={styles.tileIcon}>
              <Shield size={16} strokeWidth={1.4} />
            </div>
            <h3 className={styles.tileName}>ACCOUNT</h3>
            <p className={styles.tileDesc}>Authentication, profile, and access management for PRODIGIUM_OS.</p>
            <div className={styles.tileStatus}>
              <span className={styles.dotLive} />
              Live
            </div>
            <span className={styles.cornerTR} />
          </Link>

          {/* ── GAMES — 1×1 ─────────────────────────────────── */}
          {/* Games now lives at its own site — Null Arcade (external) */}
          <a
            href="https://nullarcade.space/"
            target="_blank"
            rel="noopener noreferrer"
            className={`${styles.tile} ${styles.tileGames}`}
          >
            <div className={styles.tileIcon}>
              <Gamepad2 size={16} strokeWidth={1.4} />
            </div>
            <h3 className={styles.tileName}>GAMES</h3>
            <div className={styles.tileStatus}>
              <span className={styles.dotAmber} />
              Beta ↗
            </div>
            <span className={styles.cornerTR} />
          </a>
          {/* ── STUDIO — 1×1 ────────────────────────────────── */}
          <Link to="/studio" className={`${styles.tile} ${styles.tileStudio}`}>

            <div className={styles.tileIcon}>
              <Layers size={16} strokeWidth={1.4} />
            </div>
            <h3 className={styles.tileName}>STUDIO</h3>
          </Link>

          {/* ── BANNER — 4×2 full-width image carousel ─────── */}
          <div className={`${styles.tile} ${styles.tileBanner}`}>
            <img
              src="/banner-bg.jpg"
              alt=""
              className={`${styles.bannerImg} ${bannerIdx === 0 ? styles.bannerImgActive : ""}`}
              loading="eager"
              decoding="async"
            />
            <img
              src="/banner-1.jpg"
              alt=""
              className={`${styles.bannerImg} ${bannerIdx === 1 ? styles.bannerImgActive : ""}`}
              loading="lazy"
              decoding="async"
            />
            <img
              src="/banner-2.jpg"
              alt=""
              className={`${styles.bannerImg} ${bannerIdx === 2 ? styles.bannerImgActive : ""}`}
              loading="lazy"
              decoding="async"
            />
            <div className={styles.bannerGrad} />
            <div className={styles.bannerContent}>
              <p className={styles.bannerLabel}>Prodigium News</p>
              <div className={styles.bannerCarousel}>
                {NEWS.map((item, i) => (
                  <div
                    key={i}
                    className={`${styles.bannerItem} ${i === newsIdx ? styles.bannerItemActive : ""}`}
                  >
                    <span className={styles.bannerItemDate}>{item.date}</span>
                    <p className={styles.bannerItemTitle}>{item.title}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── BUSINESS TOOLS — 4×2 ────────────────────────── */}
          <Link to="/business-tools" className={`${styles.tile} ${styles.tileBiztools}`}>

            <div className={styles.biztoolsHeader}>
              <h2 className={styles.tileName}>BUSINESS TOOLS</h2>
              <span className={styles.biztoolsSub}>By Prodigium Dynamics</span>
            </div>
          </Link>

          {/* ── TELEPROMPTER CAM — 4×1 ──────────────────────── */}
          <Link to="/teleprompter" className={`${styles.tile} ${styles.tileTcam}`}>
            <div className={styles.tileIcon}>
              <Video size={16} strokeWidth={1.4} />
            </div>
            <h2 className={styles.tileName}>TELEPROMPTER CAM</h2>
            <p className={styles.tileDesc}>Hold to record. Read while you look. No cuts, no edits.</p>
            <div className={styles.tileStatus}>
              <span className={styles.dotLive} />
              Live
            </div>
            <span className={styles.cornerTR} />
          </Link>

        </div>
      </div>

      {/* ── INSTALL BOTTOM SHEET ─────────────────────────── */}
      {showInstallSheet && (
        <>
          <div
            className={styles.installOverlay}
            onClick={() => setShowInstallSheet(false)}
          />
          <div className={styles.installSheet} role="dialog" aria-modal="true" aria-label="Install Prodigium OS">

            <div className={styles.installSheetHeader}>
              <span className={styles.installSheetTag}>PRODIGIUM_OS</span>
              <button
                className={styles.installSheetClose}
                onClick={() => setShowInstallSheet(false)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className={styles.installSheetImgPlaceholder} />

            <div className={styles.installSheetBody}>
              <h2 className={styles.installSheetTitle}>Your studio, installed.</h2>
              <ul className={styles.installSheetList}>
                <li>Works offline after the first load</li>
                <li>Opens like a native app — no browser chrome</li>
                <li>Always up to date, no manual updates needed</li>
                <li>PageBurn · Skillarq · Brubri · Burn Rate — all in one place</li>
              </ul>
            </div>

            <div className={styles.installSheetFooter}>
              {installMsg
                ? <p className={styles.installSheetMsg}>{installMsg}</p>
                : (
                  <button className={styles.installSheetBtn} onClick={handleInstall}>
                    INSTALL
                  </button>
                )
              }
            </div>

          </div>
        </>
      )}
    </>
  );
}
